<template>
  <!-- View Policy Details -->
  <div
    class="modal fade dq-modal"
    id="view_policy"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg dq-modal-lg">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge dq-icon-policy">
              <i class="ti ti-file-search"></i>
            </span>

            <div class="lh-1">
              <h4 class="modal-title mb-1">Policy Details</h4>
              <div class="dq-subtitle">
                Review policy information and access the attached document.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body dq-modal-body">
          <div v-if="policy">
            <!-- Top summary card -->
            <div class="dq-summary-card mb-3">
              <div
                class="d-flex align-items-start justify-content-between gap-3 flex-wrap"
              >
                <div class="dq-title-wrap">
                  <h5 class="mb-1 dq-policy-title">{{ policy.name }}</h5>

                  <div class="d-flex align-items-center flex-wrap gap-2 mt-2">
                    <span class="dq-chip">
                      <i class="ti ti-calendar me-1"></i>
                      Created:
                      <b class="ms-1">
                        <span v-if="policy.created_at">{{
                          formattedCreatedAt
                        }}</span>
                        <span v-else>-</span>
                      </b>
                    </span>

                    <span class="dq-chip dq-chip-soft">
                      <i class="ti ti-building-community me-1"></i>
                      Department:
                      <b class="ms-1">{{
                        policy.department?.name || "All Department"
                      }}</b>
                    </span>

                    <span class="dq-chip dq-chip-muted" v-if="policy.file_url">
                      <i class="ti ti-paperclip me-1"></i>
                      {{ prettyPolicyFileName || "File attached" }}
                    </span>

                    <span class="dq-chip dq-chip-warn" v-else>
                      <i class="ti ti-alert-triangle me-1"></i>
                      No file
                    </span>
                  </div>
                </div>

                <div class="dq-quick-actions" v-if="policy.file_url">
                  <a
                    :href="policy.file_url"
                    target="_blank"
                    class="btn btn-primary dq-btn-primary btn-sm"
                  >
                    <i class="ti ti-eye me-1"></i>
                    Open
                  </a>
                  <a
                    :href="policy.file_url"
                    target="_blank"
                    :download="prettyPolicyFileName"
                    class="btn btn-outline-secondary dq-btn-outline-muted btn-sm"
                  >
                    <i class="ti ti-download me-1"></i> Download
                  </a>
                </div>
              </div>
            </div>

            <!-- Description card -->
            <div class="dq-section mb-3">
              <div class="dq-section-head">
                <div class="dq-section-badge">
                  <i class="ti ti-align-left"></i>
                </div>
                <div>
                  <div class="dq-section-title">Description</div>
                  <div class="dq-help">
                    A brief summary for quick understanding.
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <p class="mb-0 dq-description">
                  {{ policy.description || "No description provided." }}
                </p>
              </div>
            </div>

            <!-- File card -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-badge dq-section-badge-file">
                  <i class="ti ti-file"></i>
                </div>
                <div>
                  <div class="dq-section-title">Policy file</div>
                  <div class="dq-help">
                    Open in a new tab or download for offline access.
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div v-if="policy.file_url" class="dq-file-card">
                  <div class="dq-file-left">
                    <div class="dq-file-icon">
                      <i class="ti ti-file-type-pdf"></i>
                    </div>
                    <div>
                      <div class="dq-file-title">
                        {{ prettyPolicyFileName || "Attached document" }}
                      </div>
                      <div class="dq-file-sub">
                        {{ fileExt ? `${fileExt} document` : "Document" }} •
                        Click Open to preview, or Download to save.
                      </div>
                    </div>
                  </div>

                  <div class="dq-file-actions">
                    <a
                      :href="policy.file_url"
                      target="_blank"
                      class="btn btn-outline-primary dq-btn-outline btn-sm"
                    >
                      <i class="ti ti-eye me-1"></i> Open Policy
                    </a>
                    <a
                      :href="policy.file_url"
                      target="_blank"
                      :download="prettyPolicyFileName"
                      class="btn btn-outline-secondary dq-btn-outline-muted btn-sm"
                    >
                      <i class="ti ti-download me-1"></i> Download
                    </a>
                  </div>
                </div>

                <div v-else class="dq-empty-file">
                  <div class="dq-empty-icon">
                    <i class="ti ti-file-off"></i>
                  </div>
                  <div>
                    <div class="dq-empty-title">No file uploaded</div>
                    <div class="dq-help mb-0">
                      This policy doesn’t have an attachment yet.
                    </div>
                  </div>
                </div>

                <div class="dq-info-strip mt-3">
                  <i class="ti ti-lock me-2"></i>
                  Access depends on department scope and user permissions.
                </div>
              </div>
            </div>
          </div>

          <!-- Loading state -->
          <div v-else class="dq-loading">
            <div class="dq-loading-card">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Loading policy details…
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer dq-modal-footer">
          <button
            type="button"
            class="btn btn-white border dq-btn-soft"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- /View Policy Details -->
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  policy: {
    type: Object,
    default: null,
  },
});

const formattedCreatedAt = computed(() => {
  if (!props.policy?.created_at) return "";
  const d = new Date(props.policy.created_at);
  return d.toLocaleDateString();
});

const prettyPolicyFileName = computed(() => {
  const p = props.policy;
  if (!p) return "Policy.pdf";

  // 1️⃣ Policy name
  const name = (p.name || "Policy").trim();

  // 2️⃣ Department
  const department = p.department?.name?.trim() || "All Departments";

  // 3️⃣ Date (from created_at)
  let dateStr = "";
  if (p.created_at) {
    const d = new Date(p.created_at);
    dateStr = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  // 4️⃣ Build base name
  let base = `${name} — ${department}`;
  if (dateStr) base += ` — ${dateStr}`;

  // 5️⃣ Sanitize for filenames
  base = base
    .replace(/[\\/:*?"<>|]/g, "") // illegal chars
    .replace(/\s+/g, " ")
    .trim();

  return `${base}.pdf`;
});

// ✅ filename helpers (from file_path or file_url)
const fileName = computed(() => {
  const p = props.policy;
  if (!p) return "";
  const raw = p.file_path || p.file_url || "";
  if (!raw) return "";
  const cleaned = raw.split("?")[0];
  const name = cleaned.split("/").pop() || "";
  return decodeURIComponent(name);
});

const fileExt = computed(() => {
  const n = fileName.value || "";
  const parts = n.split(".");
  return parts.length > 1 ? parts.pop().toUpperCase() : "";
});
</script>

<style scoped>
/* ===== Premium modal polish (UI only) ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 16px 18px;
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 1),
    rgba(255, 255, 255, 1)
  );
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: rgba(99, 102, 241, 0.12);
  color: rgba(79, 70, 229, 1);
}
.dq-icon-policy {
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}

/* Body */
.dq-modal-body {
  padding: 16px 18px;
  max-height: 72vh;
  overflow: auto;
}

/* Summary card */
.dq-summary-card {
  border-radius: 16px;
  padding: 14px;
  background: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-policy-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.92);
  letter-spacing: -0.02em;
}
.dq-quick-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.75);
  font-size: 0.82rem;
}
.dq-chip-soft {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.14);
}
.dq-chip-muted {
  background: rgba(148, 163, 184, 0.14);
  border-color: rgba(148, 163, 184, 0.18);
}
.dq-chip-warn {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.14);
  color: rgba(120, 53, 15, 0.95);
}

/* Section */
.dq-section {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.dq-section-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(248, 250, 252, 1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-section-badge {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.1);
  color: rgba(79, 70, 229, 1);
}
.dq-section-badge-file {
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-section-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
}
.dq-section-body {
  padding: 14px;
}

.dq-description {
  color: rgba(17, 24, 39, 0.78);
  line-height: 1.55rem;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

/* File card */
.dq-file-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px;
  border-radius: 14px;
  border: 1px dashed rgba(148, 163, 184, 0.55);
  background: rgba(148, 163, 184, 0.1);
  flex-wrap: wrap;
}
.dq-file-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 240px;
}
.dq-file-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.1);
  color: rgba(220, 38, 38, 1);
  border: 1px solid rgba(239, 68, 68, 0.14);
}
.dq-file-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.88);
}
.dq-file-sub {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
}
.dq-file-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

/* Empty file */
.dq-empty-file {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 1);
}
.dq-empty-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 158, 11, 0.12);
  color: rgba(217, 119, 6, 1);
}
.dq-empty-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.88);
}

/* Info strip */
.dq-info-strip {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.82rem;
  font-weight: 700;
}

/* Loading */
.dq-loading {
  padding: 20px 0;
  display: flex;
  justify-content: center;
}
.dq-loading-card {
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 1);
  color: rgba(17, 24, 39, 0.7);
  font-weight: 700;
}

/* Footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}

.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}

.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

.dq-btn-outline {
  border-radius: 12px;
  padding: 8px 12px;
  border-width: 1px;
}
.dq-btn-outline-muted {
  border-radius: 12px;
  padding: 8px 12px;
  border-width: 1px;
}
</style>
